import {
  lazy,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useSession } from 'next-auth/react';
import { useForm } from 'react-hook-form';

import {
  FONTS,
  PPT_MAKER_CONTENT_STYLE_OPTIONS,
  PPT_MAKER_DOC_CHAR_LIMIT,
  PPTContentStyle,
  THEME_OPTIONS,
  FeatureType,
  ThemeType,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

import { streamChatCompletion } from '../api/external/GPT';
import { Button } from '../components/Button';
import {
  NumberField,
  RemoveButton,
  RemoveButtonVariant,
  SelectField,
  TextAreaField,
  TextField,
} from '../components/form-fields';
import {
  ThemeOption,
  ThemeSelectorField,
} from '../components/form-fields/ThemeSelectorField';
import GenerateButton from '../components/GenerateButton';
import GeneratePremiumConfirmationModal from '../components/GeneratePremiumConfirmationModal';
import GenericBanner from '../components/GenericBanner';
import DragDropFields from '../components/organisms/DragDropFields';
import {
  ErrorDetails,
  ErrorModalDetails,
  ErrorWithCode,
} from '../components/organisms/ErrorModalDetails';
import ModalAlerts from '../components/organisms/Modal/ModalAlerts';
import { PptSlides } from '../components/organisms/PptSlidesResult';
import PopUpModal from '../components/PopUpModal';
import SEO from '../components/SEO';
import { pageContentLocalStorageProperties } from '../constants/localStorage';
import {
  PPT_MAKER_DESCRIPTION,
  PPT_MAKER_INVALID_CONFIG_MESSAGE,
  PPT_MAKER_MAX_SECTIONS,
} from '../constants/pptMaker';
import { routes } from '../constants/routes';
import CreditContext from '../context/credits/context';
import { PageKey, PptMakerProperties } from '../enums/pageKey';
import { TOP_UP_TRACKING } from '../enums/topUpTracking';
import {
  checkInAppBrowser,
  useInAppBrowser,
} from '../hooks/useCheckInAppBrowser';
import { useGenerationTracking } from '../hooks/useGenerationTracking';
import useLocalStorageItem from '../hooks/useLocalStorageItem';
import { useModalState } from '../hooks/useModalState';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import ResultsLoader from '../ResultsLoader';
import PowerpointMakerSchema from '../schemas/PowerpointMakerSchema';
import { pptMakerProperties, PptSection } from '../types/page';
import { PptSlide } from '../types/ppt-slide';
import { signInUser } from '../utils/auth.utils';
import { downloadPpt } from '../utils/download/downloadPpt';
import { handleResponseError as handleResponseErrorUtil } from '../utils/error.utils';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';
import getLessonFormat from '../utils/lesson-format/getLessonFormat';
import { mapChatParamValues } from '../utils/page.utils';
import { scrollToResults } from '../utils/pageScroller';
import { generatePptStructure } from '../utils/pptMaker.utils';
import { setResponsePopulated } from '../utils/responsePopulatedUtil';
import { getUserCredit } from '../utils/server-side/credits';
import { clearAllLayoutCaches } from '../utils/slideBackgroundHelper';
import { EVENTS, trackEvent } from '../utils/tracking';
const AlertModal = lazy(() => import('../components/AlertModal'));

const AuthLayout = lazy(() =>
  import('../components/AuthLayout').then((mod) => ({
    default: mod.AuthLayout,
  })),
);
const Description = lazy(() => import('../components/Description'));
const SignInBtn = lazy(() =>
  import('../components/SigninBtn').then((mod) => ({ default: mod.SignInBtn })),
);

import '../fonts/Arial';
import '../fonts/Arial-Bold';
import '../fonts/Montserrat';
import '../fonts/Montserrat-Bold';
import '../fonts/Times';
import '../fonts/Times-Bold';

const AUTHENTICATED_STATUS = 'authenticated';
const AUTH_LOADING_STATUS = 'loading';
const DEFAULT_PREMIUM_CREDIT_COST =
  parseInt(process.env.NEXT_PUBLIC_GENERATE_PREMIUM_PPT_COST) || 2;

export default function Index(): JSX.Element {
  const { status } = useSession();
  const { showConfirmationModal, setShowConfirmationModal } = useModalState({});

  const isSignedIn = status === AUTHENTICATED_STATUS;
  const isAuthLoading = status === AUTH_LOADING_STATUS;
  const { refetch: refetchUserCredit } = getUserCredit();
  const { context: creditContext } = useContext(CreditContext);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [isNoCreditsError, setIsNoCreditsError] = useState(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [isModalOpen] = useState(false);

  const [slides, setSlides] = useState<PptSlide[]>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const { setItemByKey, removeItemByKey } = useLocalStorageItem();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [, setFileName] = useState('');
  const [abortController, setAbortController] = useState<AbortController>(null);
  const [selectedFont, setSelectedFont] = useState(FONTS[0]);
  const [selectedTheme, setSelectedTheme] = useState(THEME_OPTIONS[0]);

  useEffect(() => {
    if (!isSignedIn && !isAuthLoading) {
      const contentProperties =
        pageContentLocalStorageProperties[PageKey.pptMaker];
      contentProperties &&
        Object.keys(contentProperties).forEach(function (key) {
          removeItemByKey(contentProperties[key]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, creditContext]);

  useEffect(() => {
    setResponsePopulated(!!slides || isGenerating);
  }, [slides, isGenerating]);

  const { mutateAsync, isLoading } = streamChatCompletion(() => {
    setFileName('PPT');
    refetchUserCredit();
  });

  const ERROR_MODAL_TEXT =
    'An error has occurred and we have refunded your credits. Please try again later.';

  const defaultValues = {
    sections: JSON.parse(
      getLocalStorageItem(PptMakerProperties.sections, '[{"title": ""}]'),
    ) as PptSection[],
    doc: getLocalStorageItem(PptMakerProperties.doc, ''),
    slideNumber: parseInt(
      getLocalStorageItem(PptMakerProperties.slideNumber, '10'),
    ),
    contentStyle: getLocalStorageItem(
      PptMakerProperties.contentStyle,
      PPTContentStyle.DEFAULT,
    ),
  };

  const containerRef = useRef(null);

  const pptMakerForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(PowerpointMakerSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = pptMakerForm;

  const sections = watch('sections');
  const doc = watch('doc');
  const slideNumber = watch('slideNumber');
  const contentStyle = watch('contentStyle');

  const formContents = useMemo(
    () => ({
      [PptMakerProperties.doc]: doc,
      [PptMakerProperties.sections]: sections,
      [PptMakerProperties.slideNumber]: slideNumber,
      [PptMakerProperties.contentStyle]: contentStyle,
    }),
    [doc, sections, slideNumber, contentStyle],
  );

  const { trackGenerationAttempt, trackGenerationSuccess } =
    useGenerationTracking({
      pageKey: PageKey.pptMaker,
      formContents: formContents as pptMakerProperties,
    });

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  const { popUpData, setPopUpData } = useInAppBrowser();

  async function handleSubmit() {
    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    if (sections.length === 1 && slideNumber === 1) {
      setIsAlertModalOpen(true);
      return;
    }

    for (const property in formContents) {
      setItemByKey(property, formContents[property]);
    }

    const result = await trigger();
    if (!result) {
      return;
    }

    if (!isSignedIn) {
      setIsLoginModalOpen(true);
      return;
    }

    setIsConfirmationModalOpen(true);
  }

  const handlePaste = (text: string) => {
    const trimmedText = text.trim();
    if (trimmedText) {
      const textToAdd = trimmedText.slice(0, PPT_MAKER_DOC_CHAR_LIMIT);
      updateFieldValue('doc', textToAdd);
      triggerValidation('doc');
    }
  };

  function createChatParams(pageKey: PageKey, formContents, language: string) {
    const newLessonFormat = getLessonFormat(formContents, pageKey, language);
    return {
      lessonformat: newLessonFormat,
      values: mapChatParamValues(formContents, newLessonFormat),
    };
  }

  function handleConfirmClose() {
    setShowConfirmationModal(false);
    setSlides(null);
    setIsGenerating(false);
    setResponsePopulated(false);
  }

  async function handleGenerate() {
    trackGenerationAttempt();

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    const { lessonformat, values } = createChatParams(
      PageKey.pptMaker,
      formContents,
      Language.ENGLISH,
    );
    const payload = {
      chatParams: {
        values: values,
      },
      promptname: lessonformat,
      feature: FeatureType.PPT_MAKER,
      abortController: newAbortController,
    };
    setIsConfirmationModalOpen(false);
    setSlides(null);
    setCurrentSlide(0);
    setIsGenerating(true);
    clearAllLayoutCaches();

    try {
      if (creditContext.totalCredits === 0) {
        setIsNoCreditsError(true);
        return;
      }

      const result = await generatePptStructure(payload, mutateAsync);
      if (!result.success) {
        throw result.error;
      }
      setSlides(result.data);
      setCurrentSlide(0);
      trackGenerationSuccess();
    } catch (error) {
      handleResponseError(error);
      setIsGenerating(false);
    } finally {
      setAbortController(null);
    }
  }

  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  // Keeping for future reference when implementing file input
  // const handleFileContent = (content, error) => {
  //   if (error) {
  //     console.error(error);
  //     return;
  //   }
  //   updateFieldValue('doc', content);
  // };

  // const handleInputMethodChange = (method: 'text' | 'file') => {
  //   setInputMethod(method);
  //   pptMakerForm.clearErrors('content');
  // };

  function handleRedirectToTopup() {
    const paymentPageUrl = routes.PRICING;
    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
      from: TOP_UP_TRACKING.GENERATE_PREMIUM_PLAN_MODAL,
    });
    window.open(paymentPageUrl, '_blank');
  }

  const handleSignIn = () => {
    setIsLoginModalOpen(false);

    signInUser(null);
  };

  async function handleClickDownload(isPPTXDownload: boolean) {
    const downloadProps = {
      slides,
      selectedTheme,
      selectedFont,
    };

    if (isPPTXDownload) {
      downloadPpt.pptx(downloadProps);
    } else {
      downloadPpt.pdf(downloadProps);
    }
  }

  const hasErrors = Object.entries(errors).length > 0;

  const isGenerateButtonDisabled = isLoading || hasErrors;
  useScrollToError(errors);

  const banner = (
    <GenericBanner
      content={PPT_MAKER_DESCRIPTION}
      isPremium={true}
      title="Powerpoint Maker"
    />
  );

  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading && resultsRef.current) {
      scrollToResults(resultsRef);
    }
  }, [isLoading]);

  const renderResult = () => {
    const renderLoader = () => (
      <ResultsLoader message="Preparing your PPT..." />
    );

    const renderSlides = () => (
      <PptSlides
        currentSlide={currentSlide}
        handleClickDownload={handleClickDownload}
        selectedFont={selectedFont}
        selectedTheme={selectedTheme}
        setCurrentSlide={setCurrentSlide}
        setSelectedFont={setSelectedFont}
        setSelectedTheme={setSelectedTheme}
        slides={slides}
      />
    );

    const getContent = () => {
      if (isLoading || !slides) {
        return renderLoader();
      }
      return renderSlides();
    };

    return (
      isGenerating && (
        <div ref={resultsRef} className="bg-[#F8FAFD] p-4">
          {getContent()}
        </div>
      )
    );
  };

  const renderSection = (section: PptSection, index: number) => (
    <>
      <TextField
        className="flex-1"
        errorMessage={errors.sections?.[index]?.title?.message}
        id={`sections.${index}.title`}
        label={`Section ${index + 1}`}
        labelClassName="block text-base text-gray-700"
        placeholder="Section title"
        value={section.title}
        onBlur={() => {
          const newSections = [...sections];
          newSections[index].title = newSections[index].title?.trim() || '';
          updateFieldValue('sections', newSections);
          triggerValidation(`sections.${index}.title`);
        }}
        onChange={(e) => {
          const newSections = [...sections];
          newSections[index].title = e.target.value;
          updateFieldValue('sections', newSections);
          triggerValidation(`sections.${index}.title`);
        }}
      />
      <RemoveButton
        disabled={sections.length === 1}
        hasError={!!errors.sections?.[index]?.message}
        variant={RemoveButtonVariant.SECTION}
        onClick={() => {
          const newSections = sections.filter((unused, i) => i !== index);
          updateFieldValue('sections', newSections);
          triggerValidation(`sections.${index}`);
          if (errors.sections) {
            pptMakerForm.clearErrors(`sections.${index}`);
          }
        }}
      />
    </>
  );

  function handleResponseError(error: ErrorWithCode) {
    const { isNoCreditsError, hasError, errorDetails } =
      handleResponseErrorUtil(error);
    setIsNoCreditsError(isNoCreditsError);
    setHasError(hasError);
    setErrorDetails(errorDetails);
  }

  useEffect(() => {
    const contents = {
      [PptMakerProperties.doc]: doc,
      [PptMakerProperties.sections]: sections,
      [PptMakerProperties.slideNumber]: slideNumber,
      [PptMakerProperties.contentStyle]: contentStyle,
    };
    for (const property in contents) {
      setItemByKey(property, contents[property]);
    }
  }, [doc, sections, slideNumber, contentStyle, setItemByKey]);

  return (
    <>
      <SEO pageKey={PageKey.pptMaker} />
      <AuthLayout
        banner={banner}
        containerRef={containerRef}
        renderResults={renderResult}
      >
        <Description />
        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
          <TextAreaField
            showSubLabel
            className="col-span-full"
            error={!!errors.doc}
            errorMessage={!!errors.doc && 'Please enter the content'}
            id="doc"
            isMaxLengthReached={doc?.length >= PPT_MAKER_DOC_CHAR_LIMIT}
            label="Content"
            maxLength={PPT_MAKER_DOC_CHAR_LIMIT}
            name="doc"
            placeholder="Input your content here"
            rows={2}
            showPasteButton={true}
            subLabel="Enter the main content for your Powerpoint."
            type="doc"
            value={doc}
            onPaste={handlePaste}
            onBlur={() => {
              processInput(
                updateFieldValue,
                triggerValidation,
                'doc',
                doc?.trim() || '',
              );
            }}
            onChange={(e) => {
              processInput(
                updateFieldValue,
                triggerValidation,
                'doc',
                e.target.value,
              );
              formContents[PptMakerProperties.doc] = e.target.value;
            }}
          />
          <div className="col-span-full space-y-4">
            <label className="block text-lg text-gray-700">
              Add sections for your PPT
            </label>
            <DragDropFields
              droppableId="sections"
              items={sections}
              renderItem={renderSection}
              onReorder={(newSections) => {
                updateFieldValue('sections', newSections);
                triggerValidation('sections');
              }}
            />
            {sections.length < PPT_MAKER_MAX_SECTIONS && (
              <div className="col-span-full -mt-6 flex justify-center">
                <Button
                  className="'border-blue-600 inline-block w-5/6 rounded border bg-white py-1 text-sm font-medium text-blue-600 hover:bg-blue-500 hover:text-white active:bg-blue-600 active:text-white disabled:cursor-not-allowed disabled:border-blue-600 disabled:bg-white disabled:text-blue-600 disabled:opacity-60 disabled:hover:border-blue-600"
                  title="Add Section"
                  type="button"
                  disabled={
                    sections?.length >= PPT_MAKER_MAX_SECTIONS ||
                    sections.some((section) => section.title.trim() === '')
                  }
                  onClick={() => {
                    updateFieldValue('sections', [...sections, { title: '' }]);
                  }}
                >
                  Add Section
                </Button>
              </div>
            )}
          </div>
          <div className="col-span-full grid gap-y-4 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-0">
            <NumberField
              className="w-full"
              errorMessage={errors.slideNumber?.message}
              id="slideNumber"
              label="Number of Slides"
              max={40}
              min={1}
              placeholder="Slides"
              updateFn={updateFieldValue}
              validateFn={() => triggerValidation('slideNumber')}
              value={slideNumber}
            />
            <ThemeSelectorField
              highlight
              label="Theme"
              options={THEME_OPTIONS as ThemeOption[]}
              slideTitle={'Here&apos;s Your Presentation!'}
              value={selectedTheme as ThemeOption}
              className={{
                container: 'flex-col !items-start gap-0',
                label: 'text-lg',
                button: 'h-auto',
              }}
              onChange={(newTheme) => {
                clearAllLayoutCaches();

                setSelectedTheme({
                  ...newTheme,
                  id: newTheme.id as ThemeType,
                  background: newTheme.background as string,
                  text: newTheme.text as string,
                  backgroundImageTitle: newTheme.backgroundImageTitle as string,
                  backgroundImageContent: newTheme.backgroundImageContent,
                });
              }}
            />
          </div>
          <div className="col-span-full flex flex-col">
            <SelectField
              className="w-full"
              errorMessage={errors.contentStyle?.message}
              id={`contentStyle`}
              label="Content Style"
              name="contentStyle"
              placeholder="Select content style"
              value={contentStyle}
              onBlur={(e) => {
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'contentStyle',
                  e.target.value,
                );
              }}
              onChange={(value) => {
                processInput(
                  updateFieldValue,
                  triggerValidation,
                  'contentStyle',
                  value,
                );
                formContents[PptMakerProperties.contentStyle] = value;
              }}
            >
              {PPT_MAKER_CONTENT_STYLE_OPTIONS.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
            <span className="mt-3 text-base text-gray-500">
              {formContents[PptMakerProperties.contentStyle] ===
              PPTContentStyle.DEFAULT
                ? 'Content will be kept as is.'
                : 'Content will be shortened with key points.'}
            </span>
          </div>
          <div className="col-span-full">
            <div className="flex flex-col">
              <GenerateButton
                isPremium
                buttonText="Prepare PPT!"
                creditCost={DEFAULT_PREMIUM_CREDIT_COST}
                disabled={isGenerateButtonDisabled}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </AuthLayout>
      <PopUpModal {...popUpData} setIsOpen={setPopUpData} />

      <ModalAlerts
        handleConfirmClose={handleConfirmClose}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />

      <AlertModal
        hasDivider
        bodyText="To access this app's features, please log in with your google account."
        isOpen={isLoginModalOpen}
        titleText="Please log in to use this app."
        confirmButtonComponent={
          <SignInBtn
            handleSignIn={handleSignIn}
            width="w-full sm:w-auto justify-center"
          />
        }
        onCancel={() => setIsLoginModalOpen(false)}
      />
      <AlertModal
        hideCloseIcon
        isSingleButton
        bodyText={PPT_MAKER_INVALID_CONFIG_MESSAGE}
        isOpen={isAlertModalOpen}
        titleText="Invalid Presentation Configuration"
        variant="error"
        onConfirm={() => setIsAlertModalOpen(false)}
      />
      <AlertModal
        hasDivider
        hideCloseIcon
        isSingleButton
        isOpen={hasError && !isNoCreditsError}
        titleText="Something went wrong"
        variant="error"
        bodyText={
          errorDetails ? (
            <ErrorModalDetails error={errorDetails} />
          ) : (
            ERROR_MODAL_TEXT
          )
        }
        onCancel={() => setHasError(false)}
        onConfirm={() => {
          if (isModalOpen) {
            setHasError(false);
          } else {
            window.location.reload();
          }
        }}
      />

      <GeneratePremiumConfirmationModal
        creditCost={DEFAULT_PREMIUM_CREDIT_COST}
        isOpen={isConfirmationModalOpen}
        pageKey={PageKey.pptMaker}
        onCancel={() => setIsConfirmationModalOpen(false)}
        onProceed={handleGenerate}
        onRedirectToTopup={handleRedirectToTopup}
      />
    </>
  );
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
